<template>
  <div>
    <div class="container-fluid">
      <div class="page-header">
        <Breadcrumbs main="bigbuy" title="BigBuy Import" />
      </div>
      <b-modal id="BigBuyAlert" title="Admin" ok-only>
        <p class="my-4">{{ message }}</p>
      </b-modal>

      <div class="row" v-if="loadingStore">
        <div class="col-md-12">
          <div class="card p-4">Loading store settings...</div>
        </div>
      </div>

      <!-- BigBuy not enabled / no key saved -->
      <div class="row" v-if="!loadingStore && !configured">
        <div class="col-md-12">
          <div class="card p-4">
            <h5 class="mb-3">BigBuy is not configured for this store</h5>
            <p class="mb-3">
              Enable BigBuy and save the API key of the active environment in the
              store settings before importing products.
            </p>
            <router-link to="/stores/bigbuy" class="btn btn-primary" style="width: 230px;">
              Open BigBuy settings
            </router-link>
          </div>
        </div>
      </div>

      <template v-if="!loadingStore && configured">
        <!-- Toolbar -->
        <div class="row">
          <div class="col-md-12">
            <div class="card p-3">
              <div class="row">
                <div class="col-md-2">
                  <label>Environment</label>
                  <input class="form-control" :value="modeLabel" disabled />
                </div>
                <div class="col-md-2">
                  <label>Language (isoCode)</label>
                  <input class="form-control" :value="iso" disabled />
                </div>
                <div class="col-md-2">
                  <label>Page</label>
                  <input class="form-control" type="number" min="1" v-model.number="page" />
                </div>
                <div class="col-md-2">
                  <label>Page size</label>
                  <b-form-select v-model.number="pageSize" :options="pageSizeOptions"></b-form-select>
                </div>
                <div class="col-md-2">
                  <label>&nbsp;</label>
                  <button type="button" class="btn btn-primary btn-block" @click="loadPage"
                    :disabled="loadingPage || importing">
                    {{ loadingPage ? "Loading..." : "Load catalog" }}
                  </button>
                </div>
                <div class="col-md-2">
                  <label>&nbsp;</label>
                  <button type="button" class="btn btn-outline-secondary btn-block" @click="reloadCatalog"
                    :disabled="loadingPage || importing">
                    Reload
                  </button>
                </div>
              </div>
              <div class="row mt-3">
                <div class="col-md-3">
                  <label>Search (loaded page)</label>
                  <input class="form-control" type="text" v-model="search" placeholder="Name, SKU, EAN..." />
                </div>
                <div class="col-md-2">
                  <label>Margin (%)</label>
                  <input class="form-control" type="number" min="0" v-model.number="margin" />
                </div>
                <div class="col-md-2">
                  <label>EU shipping</label>
                  <input class="form-control" type="number" min="0" step="0.01" v-model.number="shippingEU" />
                </div>
                <div class="col-md-2 d-flex align-items-end">
                  <b-form-checkbox v-model="overwrite" class="mb-2">
                    Overwrite existing products
                  </b-form-checkbox>
                </div>
                <div class="col-md-3 d-flex align-items-end justify-content-end">
                  <button type="button" class="btn btn-primary" @click="startImport"
                    :disabled="!selectedCount || importing">
                    {{ importing ? "Importing..." : "Import selected (" + selectedCount + ")" }}
                  </button>
                </div>
              </div>
              <p class="text-muted mt-3 mb-0">
                Store price = (wholesale price + EU shipping) × (1 + margin / 100) — the worst-case
                shipping is baked into every price, so the Shipping menu of the admin can stay on
                Free shipping. Set the EU shipping value on the BigBuy settings page.
              </p>
              <p class="text-muted mt-3 mb-0">
                Imports are paced (~5 s per product) to respect BigBuy rate limits
                (1 request / 5 s per endpoint).
              </p>
            </div>
          </div>
        </div>

        <!-- Progress -->
        <div class="row" v-if="importing || importReport">
          <div class="col-md-12">
            <div class="card p-3">
              <div v-if="importing">
                <p class="mb-2">{{ importLabel }} ({{ importIndex }}/{{ importTotal }})</p>
                <b-progress :value="importIndex" :max="importTotal" variant="primary" animated></b-progress>
              </div>
              <div v-if="importReport" class="mt-2">
                <strong>Import finished:</strong>
                {{ importReport.created }} imported,
                {{ importReport.skipped }} skipped,
                {{ importReport.errors }} error(s).
              </div>
            </div>
          </div>
        </div>

        <!-- Catalog table -->
        <div class="row">
          <div class="col-md-12">
            <div class="card p-3">
              <div class="d-flex justify-content-between align-items-center mb-3">
                <span class="text-muted" v-if="catalogLoaded">
                  Page {{ page }} — {{ rows.length }} products loaded ({{ filteredRows.length }} shown)
                </span>
                <span class="text-muted" v-else>Load the BigBuy catalog to see products.</span>
                <div v-if="rows.length">
                  <button type="button" class="btn btn-outline-primary btn-sm mr-2" @click="selectAll"
                    :disabled="importing">
                    Select all shown
                  </button>
                  <button type="button" class="btn btn-outline-secondary btn-sm" @click="clearSelection"
                    :disabled="importing">
                    Clear selection
                  </button>
                </div>
              </div>
              <div class="table-responsive" v-if="filteredRows.length">
                <table class="table table-hover">
                  <thead>
                    <tr>
                      <th style="width: 40px;"></th>
                      <th>SKU</th>
                      <th>Name</th>
                      <th>Wholesale</th>
                      <th>Store price</th>
                      <th>Stock</th>
                      <th>Category</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="row in filteredRows" :key="row.id + '-' + row.sku">
                      <td>
                        <b-form-checkbox v-model="row._selected" :disabled="importing"></b-form-checkbox>
                      </td>
                      <td>{{ row.sku }}</td>
                      <td>{{ row.name }}</td>
                      <td>{{ row.wholesale != null ? row.wholesale.toFixed(2) : "—" }}</td>
                      <td>{{ storePrice(row) }}</td>
                      <td>{{ row.stock != null ? row.stock : "—" }}</td>
                      <td>{{ rowCategoryName(row) }}</td>
                      <td>
                        <span v-if="row._status === 'working'" class="badge badge-info">Importing...</span>
                        <span v-else-if="row._status === 'done'" class="badge badge-success">Imported</span>
                        <span v-else-if="row._status === 'skipped'" class="badge badge-secondary">Skipped (exists)</span>
                        <span v-else-if="row._status === 'error'" class="badge badge-danger" :title="row._message">
                          Error
                        </span>
                        <span v-else-if="row._exists" class="badge badge-warning"
                          title="A product with this id already exists in the store">
                          Already in store
                        </span>
                        <span v-else class="badge badge-light">—</span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p class="text-muted mb-0" v-else-if="catalogLoaded && rows.length">
                No product on this page matches your search.
              </p>
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import firebase from '@/utils/firestore-shim';
import axios from 'axios';
import Cookies from 'js-cookie';
import slug from "slug";
import { apiUrl, uploadHeaders } from '../../utils/imageUpload.js';

// BigBuy rate limits: 1 request / 5 s per catalog endpoint (productimages,
// productsinformation...). Requests of the same endpoint are paced below.
const BB_ENDPOINT_INTERVAL = 5200;
const DEFAULT_MARGIN = 30;
const DEFAULT_STOCK = 100;

function firstDefined(values) {
  for (var i = 0; i < values.length; i++) {
    var v = values[i];
    if (v !== undefined && v !== null && v !== '') return v;
  }
  return undefined;
}

function toNumber(value) {
  if (typeof value === 'number' && isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '' && isFinite(Number(value))) return Number(value);
  return undefined;
}

function toText(value) {
  return typeof value === 'string' ? value : '';
}

function round2(value) {
  return Math.round(value * 100) / 100;
}

// Collects image URLs from the various shapes returned by BigBuy
// (array of strings, array of objects with url/src/fileUrl, nested images).
function extractUrls(node) {
  var out = [];
  var walk = function (value) {
    if (!value) return;
    if (Array.isArray(value)) {
      value.forEach(walk);
      return;
    }
    if (typeof value === 'string') {
      if (/^https?:\/\//i.test(value)) out.push(value);
      return;
    }
    if (typeof value === 'object') {
      var url = firstDefined([value.url, value.src, value.image, value.fileUrl, value.imageUrl]);
      if (typeof url === 'string' && /^https?:\/\//i.test(url)) out.push(url);
      if (value.images) walk(value.images);
    }
  };
  walk(node);
  return out.filter(function (url, index) { return out.indexOf(url) === index; });
}

// Collects category names from BigBuy shapes (flat list, paths as nested
// arrays of objects...). The last name of a path is the most specific one.
function extractCategoryNames(node) {
  var out = [];
  var walk = function (value) {
    if (!value) return;
    if (Array.isArray(value)) {
      value.forEach(walk);
      return;
    }
    if (typeof value === 'string') {
      if (value.trim()) out.push(value.trim());
      return;
    }
    if (typeof value === 'object') {
      var name = firstDefined([value.name, value.title, value.category]);
      if (typeof name === 'string' && name.trim()) out.push(name.trim());
      if (value.children) walk(value.children);
      if (value.path) walk(value.path);
    }
  };
  walk(node);
  return out.filter(function (name, index) { return out.indexOf(name) === index; });
}

// Firestore document ids cannot contain "/" and a few other characters.
function sanitizeDocId(value) {
  return String(value === undefined || value === null ? '' : value)
    .trim()
    .replace(/[\/\\#?\[\]*\s]+/g, '-');
}

export default {
  data() {
    return {
      loadingStore: true,
      storeDoc: null,
      host: '',
      catalogLoaded: false,
      rows: [],
      page: 1,
      pageSize: 100,
      pageSizeOptions: [
        { value: 50, text: '50' },
        { value: 100, text: '100' },
        { value: 200, text: '200' }
      ],
      search: '',
      margin: DEFAULT_MARGIN,
      shippingEU: 0,
      overwrite: false,
      loadingPage: false,
      importing: false,
      importIndex: 0,
      importTotal: 0,
      importLabel: '',
      importReport: null,
      message: '',
      existingIds: null,
      lastCallAt: {}
    };
  },
  computed: {
    configured() {
      return !!(this.storeDoc && this.storeDoc.bigbuyEnabled && this.activeKey);
    },
    activeKey() {
      return this.storeDoc && this.storeDoc.bigbuyMode === 'production'
        ? this.storeDoc.bigbuyApiKey
        : this.storeDoc ? this.storeDoc.bigbuySandboxKey : '';
    },
    apiBase() {
      return this.storeDoc && this.storeDoc.bigbuyMode === 'production'
        ? 'https://api.bigbuy.eu'
        : 'https://api.sandbox.bigbuy.eu';
    },
    modeLabel() {
      return this.storeDoc && this.storeDoc.bigbuyMode === 'production' ? 'Production' : 'Sandbox';
    },
    iso() {
      return (this.storeDoc && this.storeDoc.bigbuyIso) || 'en';
    },
    symbol() {
      return (this.storeDoc && this.storeDoc.symbol) || '€';
    },
    filteredRows() {
      var query = this.search.trim().toLowerCase();
      if (!query) return this.rows;
      return this.rows.filter(function (row) {
        return String(row.name || '').toLowerCase().indexOf(query) !== -1 ||
          String(row.sku || '').toLowerCase().indexOf(query) !== -1 ||
          String(row.ean || '').toLowerCase().indexOf(query) !== -1;
      });
    },
    selectedCount() {
      return this.rows.filter(function (row) { return row._selected; }).length;
    }
  },
  async mounted() {
    try {
      const data = await this.getStoresData();
      if (!data.length) {
        this.$router.push('/dashboard/default');
        return;
      }
      this.storeDoc = data[0];
      this.host = data[0].host;
      var margin = Number(this.storeDoc.bigbuyMargin);
      if (isFinite(margin) && this.storeDoc.bigbuyMargin !== '' && this.storeDoc.bigbuyMargin >= 0) {
        this.margin = margin;
      }
      var shippingEU = Number(this.storeDoc.bigbuyShippingEU);
      if (isFinite(shippingEU) && this.storeDoc.bigbuyShippingEU !== '' && shippingEU >= 0) {
        this.shippingEU = shippingEU;
      }
      if (this.configured) {
        try {
          await this.loadExistingIds();
        } catch (error) {
          // The store may not have any Products collection yet: not fatal.
        }
      }
    } finally {
      this.loadingStore = false;
    }
  },
  methods: {
    sleep(ms) {
      return new Promise(function (resolve) { setTimeout(resolve, ms); });
    },
    // GET catalog BigBuy via l'API Hermes (proxy serveur) : la clé BigBuy
    // ne quitte jamais le serveur, aucun souci de CORS ni de réseau
    // navigateur. Le pacing par endpoint est conservé côté client.
    async bbGet(endpointKey, resource, params) {
      var last = this.lastCallAt[endpointKey] || 0;
      var wait = BB_ENDPOINT_INTERVAL - (Date.now() - last);
      if (wait > 0) await this.sleep(wait);
      this.lastCallAt[endpointKey] = Date.now();
      var url = apiUrl + '/api/stores/' + encodeURIComponent(this.host) + '/bigbuy/catalog/' + resource;
      var config = {
        params: params || {},
        headers: uploadHeaders(),
        timeout: 30000
      };
      try {
        return await axios.get(url, config);
      } catch (error) {
        if (error.response && error.response.status === 429) {
          await this.sleep(BB_ENDPOINT_INTERVAL + 800);
          this.lastCallAt[endpointKey] = Date.now();
          return await axios.get(url, config);
        }
        throw error;
      }
    },
    bbErrorMessage(error) {
      // L'import passe par l'API Hermes : les erreurs BigBuy arrivent dans
      // error.response.data.error (502/504 avec le code HTTP BigBuy dans le
      // texte du message).
      var detail = error && error.response && error.response.data && error.response.data.error
        ? String(error.response.data.error) : '';
      if (error.response) {
        var status = error.response.status;
        if (status === 401) return detail || "Hermes API key invalid or missing (x-api-key). Check config.json.";
        if (status === 409) return detail || "BigBuy is disabled or has no key saved for the active environment. Save the BigBuy settings first.";
        if (status === 429) return "Rate limit reached (HTTP 429). Wait a few minutes and retry.";
        if (status === 504) return detail || "BigBuy did not answer in time. Retry later.";
        if (status === 502) return detail || "BigBuy returned an error.";
        if (status === 404) return detail || "Unknown catalog resource.";
        if (status === 500) return detail || "Hermes API server error.";
        return detail || "Hermes API returned HTTP " + status + ".";
      }
      return "Unable to reach the Hermes API (" + (error && error.message ? error.message : "network error") + ").";
    },
    async loadPage() {
      if (!this.configured || this.loadingPage || this.importing) return;
      this.loadingPage = true;
      this.importReport = null;
      try {
        var params = { isoCode: this.iso, page: this.page, pageSize: this.pageSize };
        var basicRes;
        try {
          basicRes = await this.bbGet('products', 'products', params);
        } catch (error) {
          this.rows = [];
          this.catalogLoaded = false;
          this.message = this.bbErrorMessage(error);
          this.$bvModal.show('BigBuyAlert');
          return;
        }
        var infoWarning = '';
        var infoItems = [];
        try {
          var infoRes = await this.bbGet('productsinformation', 'productsinformation', params);
          infoItems = this.payloadItems(infoRes.data);
        } catch (error) {
          infoWarning = this.bbErrorMessage(error);
        }

        var basicItems = this.payloadItems(basicRes.data);
        var infoById = {};
        var infoBySku = {};
        infoItems.forEach(function (item) {
          if (!item || typeof item !== 'object') return;
          if (item.id !== undefined) infoById[String(item.id)] = item;
          if (item.sku !== undefined) infoBySku[String(item.sku)] = item;
        });
        var source = basicItems.length ? basicItems : infoItems;
        var self = this;
        this.rows = source.map(function (basic) {
          var info = null;
          if (basic && typeof basic === 'object') {
            info = infoById[String(basic.id)] || infoBySku[String(basic.sku)] || null;
          }
          return self.normalizeRow(basic, info);
        });
        if (this.existingIds) {
          this.rows.forEach(function (row) {
            row._exists = !!self.existingIds[self.rowDocId(row)];
          });
        }
        this.catalogLoaded = true;
        if (infoWarning) {
          this.message = "Products loaded without details: " + infoWarning;
          this.$bvModal.show('BigBuyAlert');
        }
      } finally {
        this.loadingPage = false;
      }
    },
    reloadCatalog() {
      this.loadPage();
    },
    // BigBuy catalogs are arrays, but this tolerates wrapped payloads
    // ({ items: [...] }, { data: [...] }...) between API versions.
    payloadItems(data) {
      if (Array.isArray(data)) return data;
      if (data && Array.isArray(data.items)) return data.items;
      if (data && Array.isArray(data.data)) return data.data;
      if (data && Array.isArray(data.products)) return data.products;
      return [];
    },
    prevPage() {
      if (this.page <= 1) return;
      this.page = this.page - 1;
      this.loadPage();
    },
    nextPage() {
      this.page = this.page + 1;
      this.loadPage();
    },
    // Builds a flat row from products.json (basic) joined with
    // productsinformation.json (details). Field names are read defensively
    // because BigBuy payloads can vary between API versions.
    normalizeRow(basic, info) {
      var b = basic && typeof basic === 'object' ? basic : {};
      var i = info && typeof info === 'object' ? info : {};
      var id = firstDefined([i.id, b.id, i.productId]);
      var sku = firstDefined([i.sku, b.sku, id]);
      var name = firstDefined([i.name, i.title, b.name, b.title, sku]);
      var wholesale = toNumber(firstDefined([i.wholesalePrice, i.price, i.costPrice, i.wholesale,
        b.wholesalePrice, b.price, b.wholesale]));
      var retail = toNumber(firstDefined([i.retailPrice, i.recommendedRetailPrice, i.msrp, i.rrp,
        b.retailPrice]));
      var stock = toNumber(firstDefined([i.stock, i.quantity, i.stockQuantity, i.stockQty, i.qty,
        b.stock, b.quantity]));
      var description = firstDefined([i.description, i.longDescription, b.description]);
      var shortDescription = firstDefined([i.shortDescription, i.descriptionShort, b.shortDescription]);
      return {
        id: id !== undefined ? String(id) : '',
        sku: sku !== undefined ? String(sku) : '',
        ean: String(firstDefined([i.ean, b.ean]) || ''),
        name: name !== undefined ? String(name) : '',
        wholesale: wholesale,
        retail: retail,
        stock: stock,
        weight: firstDefined([i.weight, b.weight]),
        description: toText(description),
        shortDescription: toText(shortDescription),
        imageUrls: extractUrls(firstDefined([i.images, b.images])),
        categories: extractCategoryNames(firstDefined([i.categories, i.category, b.categories, b.category])),
        _selected: false,
        _status: '',
        _message: '',
        _exists: false
      };
    },
    rowDocId(row) {
      return sanitizeDocId(row.sku || row.id);
    },
    // Store category (upper case, as in the admin) taken from the most
    // specific BigBuy category name.
    rowCategoryName(row) {
      var names = row.categories || [];
      var name = names.length ? String(names[names.length - 1]) : '';
      name = name.replace(/\s+/g, ' ').trim();
      if (!name) name = 'IMPORTED';
      if (name.length > 60) name = name.slice(0, 60);
      return name.toUpperCase();
    },
    storePrice(row) {
      if (row.wholesale == null) return "—";
      return round2((row.wholesale + this.shippingEU) * (1 + (Number(this.margin) || 0) / 100)).toFixed(2) + ' ' + this.symbol;
    },
    selectAll() {
      this.filteredRows.forEach(function (row) { row._selected = true; });
    },
    clearSelection() {
      this.rows.forEach(function (row) { row._selected = false; });
    },
    async loadExistingIds() {
      var snapshot = await firebase.firestore().collection('Sites').doc(this.host)
        .collection('Products').get();
      var map = {};
      snapshot.forEach(function (doc) { map[doc.id] = true; });
      this.existingIds = map;
      this.rows.forEach(function (row) {
        row._exists = !!map[sanitizeDocId(row.sku || row.id)];
      });
    },
    async startImport() {
      var selected = this.rows.filter(function (row) { return row._selected; });
      if (!selected.length) return;
      this.importing = true;
      this.importReport = null;
      this.importIndex = 0;
      this.importTotal = selected.length;
      if (!this.existingIds) {
        try {
          await this.loadExistingIds();
        } catch (error) {
          // No Products collection yet.
        }
      }
      if (!this.existingIds) this.existingIds = {};
      var created = 0;
      var skipped = 0;
      var errors = 0;
      var missingCategories = [];

      for (var k = 0; k < selected.length; k++) {
        var row = selected[k];
        this.importIndex = k + 1;
        this.importLabel = "Importing " + (row.name || row.sku);
        row._status = 'working';
        row._message = '';
        try {
          var docId = this.rowDocId(row);
          if (!docId) throw new Error("Missing SKU/id, cannot import.");
          if (this.existingIds[docId] && !this.overwrite) {
            row._status = 'skipped';
            row._message = "Product already exists in the store.";
            skipped++;
            continue;
          }
          var urls = row.imageUrls.slice();
          if (!urls.length && row.id) {
            try {
              var imgRes = await this.bbGet('productimages',
                'productimages/' + encodeURIComponent(row.id));
              urls = extractUrls(imgRes.data);
            } catch (imageError) {
              // Import without image rather than failing the whole row.
            }
          }
          var product = this.buildProductDoc(row, docId, urls);
          await firebase.firestore().collection('Sites').doc(this.host)
            .collection('Products').doc(docId).set(product);
          this.existingIds[docId] = true;
          row._status = 'done';
          row._exists = true;
          created++;
          if (missingCategories.indexOf(product.category) === -1) {
            missingCategories.push(product.category);
          }
        } catch (error) {
          row._status = 'error';
          row._message = error && error.message ? error.message : String(error);
          errors++;
        }
      }

      await this.addMissingCategories(missingCategories);
      this.importReport = { created: created, skipped: skipped, errors: errors };
      this.importing = false;
      this.importLabel = '';
    },
    // Adds the categories of imported products that do not exist yet in the
    // store (same object shape as the Categories page of the admin).
    async addMissingCategories(categories) {
      var current = Array.isArray(this.storeDoc.categories) ? this.storeDoc.categories.slice() : [];
      var added = false;
      categories.forEach(function (name) {
        if (!name) return;
        var exists = current.some(function (entry) { return entry && entry.category === name; });
        if (!exists) {
          current.push({
            category: name,
            description: '',
            image: '',
            offer: '',
            metaTitle: '',
            metaDescription: '',
            metaKeywords: ''
          });
          added = true;
        }
      });
      if (!added) return;
      try {
        await firebase.firestore().collection('Sites').doc(this.host).update({ categories: current });
        this.storeDoc.categories = current;
      } catch (error) {
        this.message = "Products imported, but adding the new categories failed: " + error;
        this.$bvModal.show('BigBuyAlert');
      }
    },
    stripHtml(value) {
      return String(value).replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 300);
    },
    fileNameFromUrl(url) {
      try {
        return decodeURIComponent(url.split('/').pop().split('?')[0]) || 'image';
      } catch (error) {
        return 'image';
      }
    },
    // Product document with the exact same fields as the "Add product"
    // window of the admin, so the storefront (ecom) renders it normally.
    buildProductDoc(row, docId, urls) {
      var marginFactor = 1 + (Number(this.margin) || 0) / 100;
      var shippingEU = Number(this.shippingEU) || 0;
      var wholesale = row.wholesale != null ? row.wholesale : 0;
      // Frais de port EU inclus dans le prix de vente (réglage BigBuy de
      // l'admin) : le menu Shipping peut rester sur « Free shipping ».
      var withShipping = wholesale + shippingEU;
      var price = round2(withShipping * marginFactor);
      var compare = row.retail != null ? round2(row.retail * marginFactor) : 0;
      if (compare <= price) compare = 0;
      var name = row.name || row.sku || docId;
      var images = urls.slice(0, 10).map(function (url, index) {
        return {
          image_id: 'bb-' + (row.id || docId) + '-' + index,
          src: url,
          alt: name,
          variant_id: [],
          filename: null
        };
      });
      var self = this;
      images.forEach(function (image) { image.filename = self.fileNameFromUrl(image.src); });
      var firstImage = images.length ? images[0].src : '';
      var category = this.rowCategoryName(row);
      return {
        id: docId,
        title: name,
        slug: slug(name, '-'),
        description: row.description || '',
        price: price,
        compare_at_price: compare,
        callToAction: 'Add to cart',
        cost_price: round2(wholesale),
        images: images,
        image1: firstImage,
        image2: images[1] ? images[1].src : '',
        image3: images[2] ? images[2].src : '',
        image4: images[3] ? images[3].src : '',
        article: [{
          title: '',
          images: [],
          section: '',
          callToAction: '',
          textColor: '#000000',
          backgroundColor: '#ffffff',
          opacity: 0.5,
          image1: '',
          image2: '',
          image3: '',
          image4: ''
        }],
        status: true,
        backgroundColor: '#ffffff',
        ReviewBackgroundColor: '#ffffff',
        ReviewTextColor: '#000000',
        addingreviews: false,
        textColor: '#000000',
        opacity: '0.5',
        vendors: [],
        contact: {
          title: '',
          firstname: false,
          lastname: true,
          address: false,
          city: true,
          postcode: false,
          country: false,
          phone: false,
          Textcolor: '#000000',
          BackgroundColor: '#ffffff',
          opacity: '0.5'
        },
        sku: row.sku || docId,
        stock: row.stock != null ? row.stock : DEFAULT_STOCK,
        weight: row.weight != null ? String(row.weight) : '',
        category: category,
        metaTitle: name,
        metaKeywords: '',
        metaDescription: row.shortDescription ? this.stripHtml(row.shortDescription) : '',
        variants: [],
        TablePriceVariants: [],
        reviews: [],
        FirstImage: firstImage,
        affiliateLink: '',
        affiliate: false,
        landing: false,
        ShowRating: true,
        ShowSimilarProduct: true,
        ShowIndicator: true,
        ShowSubscriber: true,
        bigbuy: {
          provider: 'bigbuy',
          productId: row.id,
          sku: row.sku,
          mode: this.storeDoc.bigbuyMode || 'sandbox',
          iso: this.iso,
          margin: Number(this.margin) || 0,
          shippingEU: shippingEU,
          importedAt: new Date().toISOString()
        }
      };
    },
    async getStoresData() {
      let listData = [];
      var ProductRef = await firebase.firestore().collection("Sites")
        .where("host", "==", this.getStore())
        .get()
        .then((querySnapshot) => {
          querySnapshot.forEach((doc) => {
            listData.push(
              doc.data()
            );
          });
        });
      return listData;

    },
    getStore() {
      const host = Cookies.get('hostCookie');
      if (host) {
        return host;
      } else {
        this.$router.push('/dashboard/default');
      }
    }
  }
};
</script>

<style lang="scss" scoped>
table.text-center * {
  text-align: center;
}
</style>
